"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { EnergyEquivalents } from "@/components/tools/physics-mechanics/energy-tables";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { SIG_FIG_CHOICES } from "@/lib/tool-kit/physics-mechanics/format";
import {
  DROP,
  type DropKey,
  GRAVITY_ENERGY,
  type GravityEnergyKey,
  RELEASE,
  type ReleaseKey,
  SPRING_ENERGY,
  type SpringEnergyKey,
} from "@/lib/tool-kit/physics-mechanics/potential";
import { GRAVITY_PRESETS } from "@/lib/tool-kit/physics-mechanics/projectile";
import {
  evaluateRelation,
  initialRelationState,
  type RelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";

/** English strings. A translated content file overrides these through `ui`. */
export const POTENTIAL_ENERGY_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeGravity: "Gravitational energy (PE = mgh)",
  modeSpring: "Spring energy (PE = ½kx²)",
  modeDrop: "Speed after a fall or slide",
  modeRelease: "Speed of a mass released by a spring",
  gravityHint:
    "Gravitational potential energy is measured from a reference height you choose — usually the ground or the lowest point. Heights below it are negative.",
  springHint:
    "A spring stores energy when it is stretched or compressed by x from its relaxed length. The energy is the same for stretching and compressing.",
  dropHint:
    "Energy conservation: the potential energy lost turns into kinetic energy, so v² = u² + 2gh, where h is how far the object drops. Friction and air resistance are ignored. A negative h means the object rises.",
  releaseHint:
    "A spring pushes a mass on a frictionless horizontal surface. All the stored energy ½kx² becomes kinetic energy ½mv².",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  gravity: "Gravity",
  gravityEarth: "Earth (9.80665 m/s², standard gravity)",
  gravityMoon: "Moon (1.62 m/s²)",
  gravityMars: "Mars (3.73 m/s², mean)",
  gravityCustom: "Another value",
  results: "Results",
  energy: "Potential energy PE",
  mass: "Mass m",
  gravityField: "Gravitational acceleration g",
  height: "Height h above the reference",
  stiffness: "Spring constant k",
  stretch: "Stretch or compression x",
  finalSpeed: "Final speed v",
  startSpeed: "Initial speed u",
  drop: "Height dropped h",
  gravityHintField: "Use a negative value if the object rises.",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign, or scientific notation such as 1.5e3.",
  errors: {
    "mass-not-positive": "Mass must be greater than zero.",
    "gravity-not-positive":
      "g must come out greater than zero. Check that the final speed is larger than the initial speed for a fall, and the height is not zero.",
    "height-zero":
      "A height of zero gives no energy difference, so this value cannot be found. Enter a height other than zero.",
    "energy-negative": "Spring energy cannot be negative.",
    "stiffness-not-positive": "The spring constant must be greater than zero.",
    "stretch-zero":
      "With no stretch there is no stored energy, so this value cannot be found. Enter a stretch other than zero.",
    "cannot-reach":
      "The object does not have enough energy to get that high: the speed would have to be the square root of a negative number. Try a smaller height or a larger starting speed.",
    "speed-zero":
      "At zero speed the spring's energy would have to be zero, so this value cannot be found. Enter a speed above zero.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  equivalentHeading: "The same energy in other units",
  columnUnit: "Unit",
  columnValue: "Energy",
  assumptions:
    "Assumes a uniform gravitational field (valid near a planet's surface), an ideal spring that obeys Hooke's law, and no friction or air resistance.",
} as const;

type Mode = "gravity" | "spring" | "drop" | "release";

const ENERGY_UNITS = ["J", "kJ", "kcal", "Wh", "kWh", "ftlbf"] as const;
const EARTH = { value: "9.80665", unit: "mps2" };

const INITIAL_GRAVITY = initialRelationState<GravityEnergyKey>(
  GRAVITY_ENERGY,
  "U",
  {
    m: { value: "5", unit: "kg" },
    g: EARTH,
    h: { value: "10", unit: "m" },
  }
);
const INITIAL_SPRING = initialRelationState<SpringEnergyKey>(
  SPRING_ENERGY,
  "U",
  {
    k: { value: "200", unit: "Npm" },
    x: { value: "0.25", unit: "m" },
  }
);
const INITIAL_DROP = initialRelationState<DropKey>(DROP, "v", {
  u: { value: "0", unit: "mps" },
  h: { value: "20", unit: "m" },
  g: EARTH,
});
const INITIAL_RELEASE = initialRelationState<ReleaseKey>(RELEASE, "v", {
  k: { value: "200", unit: "Npm" },
  x: { value: "0.25", unit: "m" },
  m: { value: "0.5", unit: "kg" },
});

function presetOf(entry: { value: string; unit: string }): string {
  if (entry.unit === "mps2") {
    for (const [name, g] of Object.entries(GRAVITY_PRESETS)) {
      if (Number(entry.value) === g) {
        return name;
      }
    }
  }
  return "custom";
}

export default function PotentialEnergyCalculatorWidget() {
  const strings = useToolUi(POTENTIAL_ENERGY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("gravity");
  const [gravity, setGravity] = useState(INITIAL_GRAVITY);
  const [spring, setSpring] = useState(INITIAL_SPRING);
  const [drop, setDrop] = useState(INITIAL_DROP);
  const [release, setRelease] = useState(INITIAL_RELEASE);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const gravityLabels: Record<GravityEnergyKey, string> = {
    U: strings.energy,
    m: strings.mass,
    g: strings.gravityField,
    h: strings.height,
  };
  const springLabels: Record<SpringEnergyKey, string> = {
    U: strings.energy,
    k: strings.stiffness,
    x: strings.stretch,
  };
  const dropLabels: Record<DropKey, string> = {
    v: strings.finalSpeed,
    u: strings.startSpeed,
    h: strings.drop,
    g: strings.gravityField,
  };
  const releaseLabels: Record<ReleaseKey, string> = {
    v: strings.finalSpeed,
    k: strings.stiffness,
    x: strings.stretch,
    m: strings.mass,
  };
  const gravityEval = evaluateRelation(GRAVITY_ENERGY, gravity);
  const springEval = evaluateRelation(SPRING_ENERGY, spring);
  const dropEval = evaluateRelation(DROP, drop);
  const releaseEval = evaluateRelation(RELEASE, release);

  const gravityOptions = [
    { value: "earth", label: strings.gravityEarth },
    { value: "moon", label: strings.gravityMoon },
    { value: "mars", label: strings.gravityMars },
    { value: "custom", label: strings.gravityCustom },
  ];
  const preset = (state: RelationState<"g" | string>) =>
    presetOf(state.values.g as { value: string; unit: string });
  const withPreset = <K extends string>(
    state: RelationState<K>,
    set: (next: RelationState<K>) => void,
    name: string
  ) => {
    if (name === "custom") {
      return;
    }
    const g = GRAVITY_PRESETS[name as keyof typeof GRAVITY_PRESETS];
    set({
      ...state,
      values: { ...state.values, g: { value: String(g), unit: "mps2" } },
    });
  };
  const energyTable = (joules: number) => (
    <EnergyEquivalents
      columnUnit={strings.columnUnit}
      columnValue={strings.columnValue}
      fmt={fmt}
      heading={strings.equivalentHeading}
      joules={joules}
      sig={sig}
      unitIds={ENERGY_UNITS}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pe-mode"
        onChange={setMode}
        options={[
          { value: "gravity", label: strings.modeGravity },
          { value: "spring", label: strings.modeSpring },
          { value: "drop", label: strings.modeDrop },
          { value: "release", label: strings.modeRelease },
        ]}
        value={mode}
      />
      <p className={HINT}>
        {
          {
            gravity: strings.gravityHint,
            spring: strings.springHint,
            drop: strings.dropHint,
            release: strings.releaseHint,
          }[mode]
        }
      </p>
      {mode === "gravity" ? (
        <>
          <SelectField
            id="pe-gravity-gravity"
            label={strings.gravity}
            onChange={(name) => withPreset(gravity, setGravity, name)}
            options={gravityOptions}
            value={preset(gravity)}
          />
          <RelationInputs
            {...common}
            def={GRAVITY_ENERGY}
            evaluation={gravityEval}
            idPrefix="pe-gravity"
            labels={gravityLabels}
            onState={setGravity}
            state={gravity}
          />
        </>
      ) : null}
      {mode === "spring" ? (
        <RelationInputs
          {...common}
          def={SPRING_ENERGY}
          evaluation={springEval}
          idPrefix="pe-spring"
          labels={springLabels}
          onState={setSpring}
          state={spring}
        />
      ) : null}
      {mode === "drop" ? (
        <>
          <SelectField
            id="pe-drop-gravity"
            label={strings.gravity}
            onChange={(name) => withPreset(drop, setDrop, name)}
            options={gravityOptions}
            value={preset(drop)}
          />
          <RelationInputs
            {...common}
            def={DROP}
            evaluation={dropEval}
            hints={{ h: strings.gravityHintField }}
            idPrefix="pe-drop"
            labels={dropLabels}
            onState={setDrop}
            state={drop}
          />
        </>
      ) : null}
      {mode === "release" ? (
        <RelationInputs
          {...common}
          def={RELEASE}
          evaluation={releaseEval}
          idPrefix="pe-release"
          labels={releaseLabels}
          onState={setRelease}
          state={release}
        />
      ) : null}
      <div className="w-60">
        <SelectField
          id="pe-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "gravity" ? (
          <RelationResults
            {...common}
            def={GRAVITY_ENERGY}
            evaluation={gravityEval}
            extras={(all) => (
              <>
                {energyTable(all.U)}
                <p className={HINT}>{strings.assumptions}</p>
              </>
            )}
            labels={gravityLabels}
            state={gravity}
          />
        ) : null}
        {mode === "spring" ? (
          <RelationResults
            {...common}
            def={SPRING_ENERGY}
            evaluation={springEval}
            extras={(all) => (
              <>
                {energyTable(all.U)}
                <p className={HINT}>{strings.assumptions}</p>
              </>
            )}
            labels={springLabels}
            state={spring}
          />
        ) : null}
        {mode === "drop" ? (
          <>
            <RelationResults
              {...common}
              def={DROP}
              evaluation={dropEval}
              labels={dropLabels}
              state={drop}
            />
            <Notice>{strings.assumptions}</Notice>
          </>
        ) : null}
        {mode === "release" ? (
          <>
            <RelationResults
              {...common}
              def={RELEASE}
              evaluation={releaseEval}
              labels={releaseLabels}
              state={release}
            />
            <Notice>{strings.assumptions}</Notice>
          </>
        ) : null}
      </section>
    </>
  );
}
